import { RefObject, useCallback, useEffect, useState } from 'react';
import { TypewriterOptions } from './runTypewriter';
import { useTypewriterEngine } from './useTypewriterEngine';

interface Options extends TypewriterOptions {
    staticText: string;
    ariaLabel?: string;
    active?: boolean;
    selector?: string;
}

const DEFAULT_SELECTOR = 'input';

export const useTypewriterPlaceholder = (
    containerRef: RefObject<HTMLElement>,
    phrases: string[],
    { staticText, ariaLabel = staticText, active = true, selector = DEFAULT_SELECTOR, ...options }: Options
) => {
    const [isInteracting, setIsInteracting] = useState(false);

    const getInputs = useCallback(() => {
        const root = containerRef.current;

        if (!root) {
            return [];
        }

        const matched = Array.from(root.querySelectorAll<HTMLInputElement>(selector));

        return matched.length ? matched : Array.from(root.querySelectorAll<HTMLInputElement>(DEFAULT_SELECTOR));
    }, [containerRef, selector]);

    const writePlaceholder = useCallback(
        (text: string) => getInputs().forEach((input) => (input.placeholder = text)),
        [getInputs]
    );

    useEffect(() => {
        const inputs = getInputs();

        if (!inputs.length) {
            return undefined;
        }

        const handleFocus = () => setIsInteracting(true);
        const handleBlur = (event: FocusEvent) => setIsInteracting(Boolean((event.target as HTMLInputElement).value));

        inputs.forEach((input) => {
            input.setAttribute('aria-label', ariaLabel);
            input.addEventListener('focus', handleFocus);
            input.addEventListener('blur', handleBlur);
        });

        return () =>
            inputs.forEach((input) => {
                input.removeEventListener('focus', handleFocus);
                input.removeEventListener('blur', handleBlur);
            });
    }, [getInputs, ariaLabel]);

    const isRunning = useTypewriterEngine(phrases, active && !isInteracting, writePlaceholder, options);

    useEffect(() => {
        if (!isRunning) {
            writePlaceholder(staticText);
        }
    }, [isRunning, staticText, writePlaceholder]);

    return isRunning;
};
